import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Btn } from "@/lib/bricks";
import { Page, PageHeader, Stats, Section, List, Row, Empty, TextLink, Loading, Meta, usePalette, fieldStyle, FieldLabel } from "@/components/calm/Calm";
import { humanDate } from "@/components/developer/siteTimelineModel";
import { localDay } from "@/lib/localDay";
import { propertyCopy } from "@/lib/clientTypes";
import BookVisitDialog from "@/components/property/BookVisitDialog";
import OpsReport from "@/components/property/OpsReport";
import { SERVICES, servicesFor, modeFor, money, label, addDays, SEGMENT } from "./propertyOps";

// One building, property or REIT holding, run the way that client runs it:
// a unit turnover board, a space inventory, or a baseline-and-watch record.
// Booking opens checkout here; reports open as printable paper.
const VISIT_STATUS = { pending: "Requested", scheduled: "Scheduled", completed: "Delivered", failed: "Not done" };
const MODE_FAMILY = { property_manager: "property_manager", commercial: "commercial", investor: "investor" };

export default function PropertyOpsPage() {
  const { id } = useParams();
  const [q, setQ] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { p, dark } = usePalette();
  const kind = user?.account_type;
  const [project, setProject] = useState(null);
  const [port, setPort] = useState(null);
  const [view, setView] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState("");
  const [book, setBook] = useState(null);
  const [lossDate, setLossDate] = useState(addDays(localDay(), -1));

  const row = ((port && port.properties) || []).find((x) => x.project_id === id) || null;
  const reitRow = ((port && port.reit && port.reit.properties) || []).find((x) => x.project_id === id) || null;
  const mode = modeFor(kind, reitRow && reitRow.segment);
  const discount = (port && port.reit && port.reit.discount_pct) || 0;

  const load = useCallback(() => {
    setError("");
    Promise.all([api.get(`/dev/projects/${id}`), api.get("/dev/ops/portfolio"), api.get(`/dev/projects/${id}/timeline`).catch(() => ({ data: null }))])
      .then(([a, b, c]) => { setProject(a.data); setPort(b.data); setTimeline(c.data); })
      .catch((e) => { setProject(false); setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn’t load this property."); });
  }, [id]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!mode) return;
    const v = mode === "property_manager" ? "board" : mode === "commercial" ? "leasing" : "baseline";
    let live = true;
    api.get(`/dev/projects/${id}/ops`, { params: { view: v } }).then((r) => { if (live) setView(r.data); }).catch(() => { if (live) setView({}); });
    return () => { live = false; };
  }, [id, mode]);

  const copy = propertyCopy(kind);
  const report = q.get("report");
  const openReport = (v, extra = {}) => setQ((s) => { const n = new URLSearchParams(s); n.set("report", v); Object.entries(extra).forEach(([k, x]) => n.set(k, x)); return n; });
  const closeReport = useCallback(() => setQ((s) => { const n = new URLSearchParams(s); ["report", "unit", "loss_date"].forEach((k) => n.delete(k)); return n; }), [setQ]);
  const booked = q.get("booked") === "1";
  const wantBook = q.get("book") === "1";

  // ?book=1 (from the hub / sidebar "Book" button) opens the main booking
  // dialog once the property has loaded, then drops the flag from the URL.
  useEffect(() => {
    if (!wantBook || !project || !port || !mode) return;
    const f = MODE_FAMILY[mode];
    const all = servicesFor(f);
    const dflt = servicesFor(f, "property")[0] || servicesFor(f, "building")[0] || servicesFor(f, "unit")[0];
    if (dflt) setBook({ services: all, preset: { service_key: mode === "property_manager" ? "pm_turnover" : dflt } });
    setQ((s) => { const n = new URLSearchParams(s); n.delete("book"); return n; }, { replace: true });
  }, [wantBook, project, port, mode, setQ]);

  if (project === null || (project && !port)) return <Page testid="property-ops"><Loading /></Page>;
  if (project === false) return <Page testid="property-ops"><PageHeader title="Property" /><div role="alert" style={{ color: "var(--c-attn)", fontSize: 13 }}>{error}</div></Page>;

  const fam = MODE_FAMILY[mode];
  const unitWord = mode === "commercial" ? "space" : "unit";
  const unitList = mode === "commercial" ? ((view && view.spaces) || []).map((x) => ({ lot_id: x.lot_id, unit: x.suite, sqft: x.sqft, status_label: x.status_label }))
    : ((view && view.units) || []).map((u) => ({ lot_id: u.lot_id, unit: u.unit, sqft: u.sqft, status_label: u.status_label }));
  const svc = (scope) => servicesFor(fam, scope);
  const visits = ((timeline && timeline.visits) || []).slice().sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))).slice(0, 6);
  const propertyDefault = svc("property")[0] || svc("building")[0] || svc("unit")[0];

  let stats = [];
  if (mode === "property_manager" && view) {
    const c = view.counts || {};
    stats = [{ label: "Units", value: (view.units || []).length }, { label: "Vacant", value: c.Vacant || 0 }, { label: "Need a visit", value: view.needs_action || 0, attention: (view.needs_action || 0) > 0 }];
  } else if (mode === "commercial" && view) {
    const sp = view.spaces || [];
    stats = [{ label: "Spaces", value: sp.length }, { label: "Square feet available", value: (view.available_sqft || 0).toLocaleString("en-CA") },
      { label: "Leasing media ready", value: `${sp.filter((x) => x.ready).length} of ${sp.filter((x) => x.status !== "sold").length}` }];
  } else if (mode === "investor") {
    stats = [{ label: "Baseline", value: row && row.baseline ? humanDate(row.baseline.date) : "None" },
      { label: "Last visit", value: row && row.last_visit ? humanDate(row.last_visit.date) : "None" },
      { label: "Next check due", value: row && row.next_due ? humanDate(row.next_due) : "Not on watch", attention: !!(row && row.overdue) }];
  }

  return (
    <Page testid="property-ops" width={1100}>
      <div style={{ fontSize: 13, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <Link to="/developer/overview" style={{ color: p.ink3, textUnderlineOffset: 3 }}>{`← ${copy.things}`}</Link>
        <Link to={`/developer/projects?manage=${id}`} data-testid="ops-settings" style={{ color: p.ink3, textUnderlineOffset: 3 }}>{`Plan, address and ${(copy.units || "units").toLowerCase()}`}</Link>
      </div>
      <PageHeader title={project.name}
        subtitle={[project.address, reitRow ? SEGMENT[reitRow.segment] : null].filter(Boolean).join(" · ")}
        action={propertyDefault ? <Btn variant="accent" dark={dark} data-testid="ops-book" onClick={() => setBook({ services: svc(), preset: { service_key: mode === "property_manager" ? "pm_turnover" : propertyDefault } })}>Book a visit</Btn> : null} />

      {booked && <div data-testid="ops-booked" role="status" style={{ padding: "14px 16px", border: `1px solid ${p.hair}`, background: p.surf, fontSize: 14 }}>
        Payment received. The visit shows below once we confirm the time with your contact.</div>}
      {error && <div role="alert" style={{ fontSize: 13, color: "var(--c-attn)" }}>{error}</div>}
      {!mode && <Empty>This page is for property accounts.</Empty>}
      {mode && !view ? <Loading /> : null}
      {mode && view ? <Stats testid="ops-stats" items={stats} /> : null}

      {mode === "property_manager" && view && (
        <>
          <Section title="Units" testid="ops-units" note="Where each unit is in its turnover, from the reports on file."
            action={<TextLink testid="ops-owner-report" onClick={() => openReport("owner")}>Owner report</TextLink>}>
            {!(view.units || []).length ? <Empty>No units yet. Add them in {copy.things}.</Empty> : (
              <List>
                {view.units.map((u) => (
                  <Row key={u.lot_id || u.unit} testid="ops-unit"
                    title={`Unit ${u.unit}`}
                    meta={[u.sqft ? `${u.sqft.toLocaleString("en-CA")} sq ft` : null, u.rent_cents ? `${money(u.rent_cents)}/mo` : null,
                      u.last_condition ? `Condition report ${humanDate(u.last_condition.date)}` : "No condition report"].filter(Boolean)}
                    status={u.status_label} attention={!!u.book}
                    next={<div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", alignItems: "baseline" }}>
                      <Meta parts={[{ text: u.next_step, attention: !!u.book }]} />
                      {u.last_condition && <TextLink testid="ops-condition" onClick={() => openReport("condition", { unit: u.unit })}>Report</TextLink>}
                      {u.book && <TextLink testid="ops-unit-book" onClick={() => setBook({ services: svc("unit"), preset: { service_key: u.book, lot_id: u.lot_id } })}>{`Book ${label(u.book).toLowerCase()}`}</TextLink>}
                    </div>} />
                ))}
              </List>
            )}
          </Section>
          <Section title="Building" note="Exterior, lobby, amenities and an aerial for every listing.">
            <List><Row title={SERVICES.pm_building_set.name} meta={[`${money(SERVICES.pm_building_set.cents)} plus HST`]}
              aside={<TextLink onClick={() => setBook({ services: svc("building"), preset: { service_key: "pm_building_set" } })}>Book</TextLink>} /></List>
          </Section>
        </>
      )}

      {mode === "commercial" && view && (
        <>
          <Section title="Spaces" testid="ops-spaces" note="Leasing media for each space. Ready means photos and a floor plan are on file."
            action={<span style={{ display: "inline-flex", gap: 16 }}>
              <TextLink testid="ops-leasing" onClick={() => openReport("leasing")}>Leasing package</TextLink>
              <TextLink testid="ops-csv" onClick={() => downloadCsv(id, project.name)}>Listing sheet (CSV)</TextLink>
            </span>}>
            {!(view.spaces || []).length ? <Empty>No spaces yet. Add them in {copy.things}.</Empty> : (
              <List>
                {view.spaces.map((x) => (
                  <Row key={x.lot_id || x.suite} testid="ops-space"
                    title={`Suite ${x.suite}`}
                    meta={[`${(x.sqft || 0).toLocaleString("en-CA")} sq ft`, x.asking_cents ? `${money(x.asking_cents)}/sq ft/yr` : null, x.last_captured ? `Captured ${humanDate(x.last_captured)}` : null].filter(Boolean)}
                    next={[{ text: x.status === "sold" ? "Leased." : x.ready ? [x.virtual_visit_url ? "Virtual Visit" : null, x.floor_plan_url ? "floor plan" : null, `${x.photos.length} photos`].filter(Boolean).join(", ") : "Needs leasing media.", attention: !!x.book }]}
                    status={x.status_label} attention={!!x.book}
                    aside={x.book ? <TextLink testid="ops-space-book" onClick={() => setBook({ services: svc("unit"), preset: { service_key: x.book, lot_id: x.lot_id } })}>Book leasing set</TextLink> : null} />
                ))}
              </List>
            )}
          </Section>
          <Section title="Aerial">
            <List><Row title={view.aerial ? `Aerial set, ${humanDate(view.aerial.date)}` : "No aerial yet"} meta={[`${money(SERVICES.cre_aerial.cents)} plus HST`]}
              aside={<TextLink onClick={() => setBook({ services: svc("property"), preset: { service_key: "cre_aerial" } })}>{view.aerial ? "Book a new aerial" : "Book"}</TextLink>} /></List>
          </Section>
        </>
      )}

      {mode === "investor" && view && (
        <Section title="Baseline and latest" testid="ops-baseline" note={view.note || `${view.days_between} days between the two visits.`}
          action={view.baseline ? <TextLink testid="ops-baseline-report" onClick={() => openReport("baseline")}>Open report</TextLink> : null}>
          {!view.baseline ? <Empty>No baseline yet. An acquisition capture gives you a dated record of the property as bought.</Empty> : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
              {(view.pairs || []).slice(0, 3).map((x, i) => (
                <figure key={i} style={{ margin: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                    {[x.baseline, x.latest].map((f, j) => f ? <img key={j} src={f.preview_url || f.url} alt={`${x.kind_label}, ${j ? "latest" : "baseline"}`} style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", display: "block" }} /> : <div key={j} />)}
                  </div>
                  <figcaption style={{ fontSize: 12.5, color: p.ink3, marginTop: 6 }}>{x.kind_label}</figcaption>
                </figure>
              ))}
            </div>
          )}
        </Section>
      )}

      {mode && (
        <Section title="Insurance claim pack" testid="ops-claim" note="The last record before a date of loss and the first one after, with dated photos.">
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <FieldLabel htmlFor="ops-loss">Date of loss</FieldLabel>
              <input id="ops-loss" type="date" value={lossDate} max={localDay()} onChange={(e) => setLossDate(e.target.value)} style={fieldStyle(p)} />
            </div>
            <Btn variant="ghost" dark={dark} data-testid="ops-claim-open" disabled={!lossDate} onClick={() => openReport("claim", { loss_date: lossDate })}>Build the pack</Btn>
          </div>
        </Section>
      )}

      <Section title="Visits" action={<TextLink onClick={() => navigate("/developer/projects")}>All files</TextLink>}>
        {!visits.length ? <Empty>{copy.deliveries}</Empty> : (
          <List>
            {visits.map((v) => (
              <Row key={v.id} wide={false} title={v.service_key === "inv_watch_quarterly" ? "Portfolio visit" : label(v.service_key)}
                meta={[{ text: v.date ? humanDate(v.date) : "Date to be set" }, v.unit ? `Unit ${v.unit}` : null].filter(Boolean)}
                status={VISIT_STATUS[v.status] || v.status} attention={v.status === "failed"} />
            ))}
          </List>
        )}
      </Section>

      {book && <BookVisitDialog open onClose={() => setBook(null)} projectId={id} services={book.services} units={unitList}
        unitWord={unitWord} preset={book.preset} discountPct={discount} />}
      {report && <OpsReport projectId={id} view={report} query={{ unit: q.get("unit"), loss_date: q.get("loss_date") }}
        property={{ name: project.name, address: project.address }} onClose={closeReport} />}
    </Page>
  );
}

async function downloadCsv(id, name) {
  const r = await api.get(`/dev/projects/${id}/ops`, { params: { view: "leasing", format: "csv" }, responseType: "text", transformResponse: (x) => x });
  const blob = new Blob([typeof r.data === "string" ? r.data : String(r.data)], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${(name || "listing").replace(/[^\w-]+/g, "-").toLowerCase()}-listing-sheet.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
